import { useAuth0Profile } from "../auth0-integration/hooks/useAuth0Profile";
import LoadingScreen from "../auth0-integration/components/LoadingScreen";
import PrivateAppRoutes from "../components/routes/PrivateRoutes";
import OnboardingPage from "../pages/OnboardingPage";
import useSessionManagement from "../hooks/useSessionManagement";
import { useSocketAuthListener } from "../pubsub";
import { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../store";
import { RootState } from "../store";
import { GetNotifications } from "../store/actions/Notifications";
import { useNotificationSync } from "../hooks/useNotificationSync";
import { initializeCSRFToken } from "./apis";
import { fetchMenuStructure } from "../store/actions/MenuActions";
import { fetchMyAccess, isUsableAccess, fetchAvailablePlans } from "../store/actions/PlanActions";
import "./notificationDebugger"; // Initialize debugger for console access
import Button from "../components/shared/TailwindComponents/Button";
import { PiExclamationMarkLight } from "react-icons/pi";

export default function AppShell() {
  const { loading: authLoading, error, needsOnboarding } = useAuth0Profile();
  const isUserLoaded = useSelector((state: RootState) => !!state.user?.userInfo?.UserID);
  const dispatch = useAppDispatch();

  // Set up global session management listener
  useSessionManagement();

  // Set up notification sync on page navigation
  useNotificationSync();

  // Set up auth event listener for socket events (token expiration, profile API failures, etc.)
  useSocketAuthListener({
    autoLogout: true,
    showNotification: (message, type) => {
      // Toast notifications are already set up in App.tsx via ToastContainer
      // Auth events will trigger logout and redirect to login
    },
  });

  // Trigger menu fetch when user is loaded (login + profile done)
  const isEU = window.location.hostname.includes('eu.pons.io');

  useEffect(() => {
    if (isUserLoaded && !authLoading) {
      dispatch(fetchMenuStructure());
      // Bootstrap license data into Redux for license-gating
      // Skip on EU (licensing not active) and during onboarding (no org yet)
      if (!isEU && !needsOnboarding) {
        dispatch(fetchMyAccess());
        dispatch(fetchAvailablePlans());
      }
    }
  }, [isUserLoaded, authLoading, needsOnboarding, isEU, dispatch]);

  /*
    THE ENTITLEMENTS HAVE TO ARRIVE, and the first ask can miss.

    `isUserLoaded` means Auth0 has a user — it does not mean the PONS session
    cookie exists yet. `useAuth0Profile` creates that with `POST /auth/login`,
    and the licence call above can go out ahead of it, in which case the
    server has no session to resolve an org from and the answer is not usable.

    One retry, once, a beat later. It costs one request on a healthy load
    (`isUsableAccess` is already true, so this does nothing) and it is the
    difference between a locked product and a working one when the race is
    lost. Not a poll: if the second ask fails too, the gate stays open,
    which is the safe side.
  */
  const licenseLoaded = useSelector((s: RootState) => isUsableAccess(s.license?.access));
  useEffect(() => {
    if (isEU || needsOnboarding || !isUserLoaded || authLoading || licenseLoaded) return;
    const retry = setTimeout(() => { dispatch(fetchMyAccess()); }, 1500);
    return () => clearTimeout(retry);
  }, [isUserLoaded, authLoading, needsOnboarding, isEU, licenseLoaded, dispatch]);

  // Initialize CSRF token and notifications when user is loaded
  useEffect(() => {
    if (isUserLoaded && !authLoading) {
      // initializeCSRFToken();
      // dispatch(GetNotifications());
    }
  }, [isUserLoaded, authLoading, dispatch]);

  // Calculate overall loading state
  const isLoadingAllApis = authLoading;

  // Clear all auth/session data and reload for a clean retry
  const handleRetry = useCallback(() => {
    // Clear Auth0 SPA SDK cached tokens from localStorage
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('@@auth0spajs@@')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear session data from sessionStorage
    sessionStorage.removeItem('pons_session_active');

    // Clear session cookie
    document.cookie = 'pons_session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    // Reload for a fresh start
    window.location.reload();
  }, []);

  if (isLoadingAllApis) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-sunken px-4">
        <div className="max-w-md text-center">
          <div className="mb-4 text-danger-600 dark:text-danger-400">
            <PiExclamationMarkLight className="mx-auto h-12 w-12" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Couldn't Load Profile
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <Button
            onClick={handleRetry}
            variant="primary"
            size="small"
            className="mx-auto"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (needsOnboarding) {
    return <OnboardingPage />;
  }

  return (
    <>
      <PrivateAppRoutes />
    </>
  );
}
